import { typedQueryClient, usePermanentRealTimeUser, useTypedMutation } from "hooks";
import { APPOINTMENT_NOTE_TYPES } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { toISOString } from "utils/dateHelperFunctions";

import type { AppointmentNote } from "models";

export type AppointmentNoteFields = Pick<
  AppointmentNote,
  "appointment_id" | "name" | "email" | "note" | "phone_nr" | "backorder_date" | "visible_to_mechanic" | "attachments"
>;

type BuildAddAppointmentNoteRequestProps = {
  type: APPOINTMENT_NOTE_TYPES;
  fields: AppointmentNoteFields;
};

type AddAppointmentNotePayload = {
  type: APPOINTMENT_NOTE_TYPES;
  fields: AppointmentNoteFields;
  request: NonNullable<ReturnType<typeof generateAddAppointmentNoteRequest>>;
};

export const generateAddAppointmentNoteRequest = ({
  type,
  fields: { appointment_id, note, attachments, backorder_date, visible_to_mechanic, phone_nr, email, name }
}: BuildAddAppointmentNoteRequestProps) => {
  const requestByType: Record<APPOINTMENT_NOTE_TYPES, { path: string; payload: object } | null> = {
    [APPOINTMENT_NOTE_TYPES.Info]: { path: "/status/set_info", payload: { appointment_id, note: { note, attachments } } },
    [APPOINTMENT_NOTE_TYPES.BackOrder]: { path: "/status/set_backorder", payload: { appointment_id, note: { note, attachments, backorder_date } } },
    [APPOINTMENT_NOTE_TYPES.MakeAnOffer]: { path: "/status/set_make_offer", payload: { appointment_id, note, note_attachments: attachments } },
    [APPOINTMENT_NOTE_TYPES.WO]: { path: "/notes/wo/create", payload: { appointment_id, note, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.Main]: { path: "/notes/main/create", payload: { appointment_id, note, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.CallCustomer]: { path: "/notes/call/create", payload: { appointment_id, note, phone_nr, visible_to_mechanic, attachments } },
    [APPOINTMENT_NOTE_TYPES.Bill]: { path: "/notes/bill/create", payload: { appointment_id, note, attachment: attachments?.[0] } },
    [APPOINTMENT_NOTE_TYPES.TempDriver]: { path: "/notes/temp_driver/create", payload: { appointment_id, note, name, email, phone_nr, attachments } },
    [APPOINTMENT_NOTE_TYPES.RecurringCar]: { path: "/notes/recurring_car/create", payload: { appointment_id, note, attachments } },
    [APPOINTMENT_NOTE_TYPES.Attachment]: { path: "/notes/attachment/create", payload: { appointment_id, note, attachments } },
    [APPOINTMENT_NOTE_TYPES.ParkingLocation]: { path: "/notes/parking/create", payload: { appointment_id, note } },
    [APPOINTMENT_NOTE_TYPES.NoDrive]: { path: "/notes/no_drive/create", payload: { appointment_id, note } },
    [APPOINTMENT_NOTE_TYPES.Tyrescanner]: null,
    [APPOINTMENT_NOTE_TYPES.DMS]: null,
    [APPOINTMENT_NOTE_TYPES.ReplacementVehicle]: null,
    [APPOINTMENT_NOTE_TYPES.HighVoltageWarning]: null,
    [APPOINTMENT_NOTE_TYPES.XentryAttachment]: null
  };

  return requestByType[type];
};

export const useAddAppointmentNoteMutation = () => {
  const { user } = usePermanentRealTimeUser();

  return useTypedMutation<AddAppointmentNotePayload, number>({
    mutationFn: async ({ request }) => (await ApiInstance.post(request.path, request.payload)).data.appointment_note_id,
    onSuccess: ({ type, fields }, id) => {
      const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (!appointment) return;

      const isNoteAlreadyInCache = appointment.notes?.some(note => note.appointment_note_type_id === type);
      if (isNoteAlreadyInCache) return;

      const now = toISOString(new Date());

      typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
        ...appointment,
        notes: [
          ...(appointment.notes ?? []),
          {
            ...fields,
            id,
            created_on: now,
            updated_on: now,
            created_by_id: user?.id ?? 0,
            updated_by_id: user?.id ?? 0,
            dms_nr: appointment.dms_nr,
            reg_nr: appointment.car?.reg_nr ?? "",
            public_account_id: null,
            rental_pickup_time: null,
            rental_drop_off_time: null,
            appointment_note_type_id: type,
            is_kiosk_check_in_note: false
          }
        ]
      });
    }
  });
};
